// @owners { team: patients }
import {
  AttestationsEndpoint,
  type AttestationsEndpointCreateParams,
} from '@alto/scriptdash/alto/patient_app/attestations/v1/attestations_endpoint';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiEndpointHandler } from '~shared/helpers/api';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import { fetchAttestationRequestsByDeliveryId } from '../helpers/fetchAttestationRequestsByDeliveryId';

const attestationsEndpoint = AttestationsEndpoint(apiEndpointHandler);

export const attestationRequestsQueryKeys = createQueryKeys('attestationRequests', {
  fetchByDeliveryId: ({ userID, deliveryID }: { userID: number | null | undefined; deliveryID: number }) => ({
    queryKey: [{ userID, deliveryID }],
    queryFn: () => fetchAttestationRequestsByDeliveryId({ deliveryID }),
  }),
});

export const useQueryAttestationRequests = ({
  userID,
  deliveryID,
}: {
  userID: number | null | undefined;
  deliveryID: number | null | undefined;
}) => {
  const { data, isLoading, isError } = useQuery({
    ...attestationRequestsQueryKeys.fetchByDeliveryId({ userID, deliveryID: deliveryID ?? 0 }),
    staleTime: staleTimes.fiveMinutes,
    enabled: !!userID && !!deliveryID,
  });

  return {
    attestationRequests: data ?? [],
    isLoading,
    isError,
  };
};

export const useCreateAttestation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: AttestationsEndpointCreateParams) => {
      const response = await attestationsEndpoint.create({ params });

      if (response.errors || response.data === null || response.data === undefined) {
        throw new Error(getErrorMessageFromResponse({ errors: response.errors ?? undefined }));
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: attestationRequestsQueryKeys.fetchByDeliveryId._def,
      });
    },
  });
};
